import { useState, useEffect } from 'react';
import { getOrganizationBrandingApi } from '../services/auth/authService';
import {
  getCachedOrgBranding,
  setCachedOrgBranding,
  applyOrgFavicon,
} from '../utils/orgBrandingCache';

/**
 * Loads org name / logo / favicon for the current origin.
 * - Login: pass `{ refreshFromApi: true }` to always call the API and refresh localStorage (still hydrates from cache first for fast paint).
 * - Other auth pages: use cache when it matches `window.location.origin`; otherwise one API call.
 *
 * @param {string} [_logContext] - Reserved for backward compatibility
 * @param {{ refreshFromApi?: boolean }} [options]
 */
export function useOrganizationBranding(_logContext = 'Auth', options = {}) {
  const { refreshFromApi = false } = options;

  const [orgBranding, setOrgBranding] = useState(() =>
    typeof window === 'undefined' ? null : getCachedOrgBranding()
  );

  useEffect(() => {
    if (orgBranding?.favicon) {
      applyOrgFavicon(orgBranding.favicon, { name: orgBranding.name });
    }
  }, [orgBranding?.favicon, orgBranding?.name]);

  useEffect(() => {
    let cancelled = false;

    const fetchAndStore = async () => {
      try {
        const res = await getOrganizationBrandingApi({
          organization_url: window.location.origin,
        });
        if (cancelled || !res?.success || !res?.data) return;
        setCachedOrgBranding(res.data);
        setOrgBranding(res.data);
      } catch (err) {
        void err;
      }
    };

    if (refreshFromApi) {
      fetchAndStore();
      return () => {
        cancelled = true;
      };
    }

    const cached = getCachedOrgBranding();
    if (cached && (cached.logo || cached.name || cached.favicon)) {
      return () => {
        cancelled = true;
      };
    }

    fetchAndStore();
    return () => {
      cancelled = true;
    };
  }, [refreshFromApi]);

  return orgBranding;
}
